import React, { useMemo } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import dayjs from 'dayjs';

import { DASH } from '../dashboard/dashboardTokens';

// Slovenčina aj čeština rozlišujú 1 / 2–4 / 5+, angličtina 1 / ostatné.
const pluralDays = (count, t) => {
    const key = count === 1 ? 'dayOne' : (count >= 2 && count <= 4) ? 'dayFew' : 'dayMany';
    return `${count} ${t(`pages.employeeProfile.rhythm.${key}`)}`;
};

// Verdikt je odvodený z rozptylu príchodov — a hneď vedľa stojí číslo, z ktorého
// vznikol, aby sa dal overiť a nebol to len dojem.
//
// Presnosť príchodov ale nesmie prekryť to, že človek väčšinu dní vôbec neprišiel:
// „chodí ako hodinky" pri štyroch odpracovaných dňoch z dvadsiatich je pravdivé
// a zároveň zavádzajúce. Pri nízkom pokrytí preto vedie nadpis pokrytie.
const verdictKey = (spreadMin, attendedDays, expectedDays) => {
    if (attendedDays === 0) return 'none';
    if (expectedDays > 0 && attendedDays / expectedDays < 0.5) return 'sparse';
    if (spreadMin == null || attendedDays < 3) return 'noData';
    if (spreadMin < 25) return 'clockwork';
    if (spreadMin < 50) return 'regular';
    if (spreadMin < 90) return 'varying';
    return 'irregular';
};

/**
 * Veta, ktorú by inak musel človek z kalendára vylúštiť sám: chodí pravidelne?
 * koľko dní chýba? koľko robí v priemere? Kalendár je dôkaz, toto je záver.
 */
const RhythmSummary = ({ profile, t }) => {
    const summary = useMemo(() => {
        const days = profile.days || [];
        const from = dayjs(profile.range.from, 'YYYY-MM-DD');
        const today = dayjs().startOf('day');
        // Budúce dni mesiaca sa nepočítajú medzi zameškané — ešte neboli.
        let to = dayjs(profile.range.to, 'YYYY-MM-DD');
        if (to.isAfter(today, 'day')) to = today;

        // Prišiel = má odpípnutý príchod. Aj keď mu smenu zavrela automatizácia
        // a hodiny nevieme, v práci bol — medzi zameškané dni nepatrí.
        const attended = new Set();
        const statusOnly = new Set();
        let weekendWork = 0;
        days.forEach((day) => {
            if (day.date > to.format('YYYY-MM-DD')) return;
            const isWeekend = (dayjs(day.date, 'YYYY-MM-DD').day() + 6) % 7 >= 5;
            if (day.firstIn) {
                attended.add(day.date);
                if (isWeekend) weekendWork += 1;
            } else if (day.statusName) {
                statusOnly.add(day.date);
            }
        });

        // Kto pravidelne robí cez víkend, tomu nemá zmysel merať dochádzku
        // len na pracovné dni — základ sa prispôsobí jeho režimu.
        const weekendWorker = weekendWork >= 3;
        let expected = 0;
        for (let cursor = from; !cursor.isAfter(to, 'day'); cursor = cursor.add(1, 'day')) {
            const isWeekend = (cursor.day() + 6) % 7 >= 5;
            if (weekendWorker || !isWeekend) expected += 1;
        }

        const attendedCount = attended.size;
        const missing = Math.max(expected - attendedCount - statusOnly.size, 0);

        return {
            attendedCount,
            expected,
            missing,
            weekendWorker,
            verdict: verdictKey(profile.totals.arrivalSpreadMin, attendedCount, expected),
        };
    }, [profile]);

    const { totals, flags } = profile;

    const facts = [
        t('pages.employeeProfile.rhythm.coverage')
            .replace('{worked}', String(summary.attendedCount))
            .replace('{total}', String(summary.expected))
            .replace('{unit}', t(summary.weekendWorker
                ? 'pages.employeeProfile.rhythm.unitDays'
                : 'pages.employeeProfile.rhythm.unitWorkdays')),
        totals.avgNetWorkStr && totals.daysWorked > 0
            ? t('pages.employeeProfile.rhythm.avgDay').replace('{value}', totals.avgNetWorkStr)
            : null,
        totals.avgArrival
            ? t('pages.employeeProfile.rhythm.arrivalAround').replace('{time}', totals.avgArrival)
            : null,
        summary.missing > 0
            ? t('pages.employeeProfile.rhythm.missing').replace('{days}', pluralDays(summary.missing, t))
            : null,
        // Bez odpípnutého odchodu nevieme hodiny — nech je vidno, z koľkých dní
        // sa priemer nerátal, a nevyzerá to ako chyba výpočtu.
        flags.autoEndedCount > 0
            ? t('pages.employeeProfile.rhythm.noEnd').replace('{days}', pluralDays(flags.autoEndedCount, t))
            : null,
    ].filter(Boolean);

    const spread = totals.arrivalSpreadMin;

    return (
        <Box sx={{ p: 2, mb: 2.5, borderRadius: `${DASH.radiusSm}px`, bgcolor: 'action.hover' }}>
            <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
                <Typography
                    sx={{
                        fontFamily: DASH.display,
                        fontSize: '1.125rem',
                        fontWeight: 600,
                        letterSpacing: '-0.015em',
                    }}
                >
                    {t(`pages.employeeProfile.rhythm.verdict.${summary.verdict}`)}
                </Typography>
                {spread != null && summary.attendedCount >= 3 && (
                    <Typography variant="caption" color="text.secondary">
                        {t('pages.employeeProfile.rhythm.spread').replace('{min}', String(spread))}
                    </Typography>
                )}
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                {facts.join('  ·  ')}
            </Typography>

        </Box>
    );
};

export default RhythmSummary;
